import { CheckIcon, LightbulbIcon, XIcon } from 'lucide-react-native';
import type { ReactNode } from 'react';

import { BulletDot } from '@/components/ui/bullet-dot';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { Pill } from '@/components/ui/pill';
import { Text } from '@/components/ui/text';
import { View } from '@/components/ui/view';
import type { InquiryGuidance as InquiryGuidanceCopy } from '../../model/surveySchema';

interface ExampleCardProps {
  title: string;
  tone: 'good' | 'bad';
  examples: { text: string; detail?: string }[];
}

const EXAMPLE_TONE = {
  good: { icon: CheckIcon, iconColor: 'text-success-600' },
  bad: { icon: XIcon, iconColor: 'text-error-600' },
} as const;

function ExampleCard({ title, tone, examples }: ExampleCardProps) {
  const styles = EXAMPLE_TONE[tone];

  return (
    <Card
      variant="outline"
      size="md"
      className="gap-3 border-outline-200 bg-background-50 md:flex-1">
      <View className="flex-row items-center gap-2">
        <Icon as={styles.icon} size="sm" className={styles.iconColor} />
        <Text size="sm" bold className="text-typography-900">
          {title}
        </Text>
      </View>
      {examples.map((example, index) => (
        <View key={`${example.text}-${index}`} className="gap-0.5">
          <Text size="sm" className="text-typography-900">
            {`“${example.text}”`}
          </Text>
          {example.detail ? (
            <Text size="xs" className="text-typography-700">
              {example.detail}
            </Text>
          ) : null}
        </View>
      ))}
    </Card>
  );
}

/** The "Helpful context to include" card, set under the example cards it complements. */
function ContextCard({ tips }: { tips: string[] }) {
  return (
    <Card variant="outline" size="md" className="gap-2 border-outline-200 bg-background-50">
      <View className="flex-row items-center gap-2">
        <Icon as={LightbulbIcon} size="sm" className="text-primary-500" />
        <Text size="sm" bold className="text-typography-900">
          Helpful context to include
        </Text>
      </View>
      <View className="flex-row flex-wrap gap-x-6 gap-y-1">
        {tips.map((tip, index) => (
          <View key={`${tip}-${index}`} className="w-full flex-row items-start gap-2 md:w-[45%]">
            <BulletDot className="mt-2" />
            <Text size="sm" className="flex-1 text-typography-700">
              {tip}
            </Text>
          </View>
        ))}
      </View>
    </Card>
  );
}

/**
 * The lead-in to an inquiry's textarea: a subtitle, topic chips, and the
 * good / less-helpful example cards with the context tips beneath them. Renders nothing for the parts the survey
 * did not author, so a survey with only a subtitle still reads cleanly.
 */
export function InquiryGuidanceIntro({ guidance }: { guidance: InquiryGuidanceCopy }): ReactNode {
  const { subtitle, topics, goodExamples, lessHelpfulExamples, contextTips } = guidance;

  const hasCards = Boolean(goodExamples?.length || lessHelpfulExamples?.length);

  if (!subtitle && !topics?.length && !hasCards && !contextTips?.length) {
    return null;
  }

  return (
    <View className="gap-3">
      {subtitle ? (
        <Text size="sm" className="text-typography-700">
          {subtitle}
        </Text>
      ) : null}

      {topics?.length ? (
        <View className="flex-row flex-wrap gap-2">
          {topics.map((topic, index) => (
            <Pill
              key={`${topic}-${index}`}
              className="border border-outline-200 bg-background-50"
              textClassName="font-normal">
              {topic}
            </Pill>
          ))}
        </View>
      ) : null}

      {hasCards || contextTips?.length ? (
        <View className="gap-3">
          {hasCards ? (
            <View className="gap-3 md:flex-row">
              {goodExamples?.length ? (
                <ExampleCard
                  title="Good questions"
                  tone="good"
                  examples={goodExamples.map(({ text, note }) => ({ text, detail: note }))}
                />
              ) : null}
              {lessHelpfulExamples?.length ? (
                <ExampleCard
                  title="Less helpful questions"
                  tone="bad"
                  examples={lessHelpfulExamples.map(({ text, reason }) => ({
                    text,
                    detail: reason,
                  }))}
                />
              ) : null}
            </View>
          ) : null}
          {contextTips?.length ? <ContextCard tips={contextTips} /> : null}
        </View>
      ) : null}
    </View>
  );
}
